import React, { useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

// Static component overrides (safe — defined at module scope).
const Paragraph = ({ node, ...props }) => (
  <p className="mb-2 last:mb-0" {...props} />
);
const Strong = ({ node, ...props }) => (
  <strong className="font-semibold" {...props} />
);
const Em = ({ node, ...props }) => <em className="italic" {...props} />;
const Ul = ({ node, ...props }) => (
  <ul className="list-disc pl-5 mb-2 space-y-1" {...props} />
);
const Ol = ({ node, ...props }) => (
  <ol className="list-decimal pl-5 mb-2 space-y-1" {...props} />
);
const Li = ({ node, ...props }) => (
  <li className="leading-snug" {...props} />
);
const H1 = ({ node, ...props }) => (
  <h1 className="text-base font-bold mb-1 mt-2" {...props} />
);
const H2 = ({ node, ...props }) => (
  <h2 className="text-sm font-bold mb-1 mt-2" {...props} />
);
const H3 = ({ node, ...props }) => (
  <h3 className="text-sm font-semibold mb-1 mt-2" {...props} />
);
const Hr = () => <hr className="my-2 border-slate-200" />;
const Table = ({ node, ...props }) => (
  <div className="overflow-x-auto my-2">
    <table className="text-xs border-collapse" {...props} />
  </div>
);
const Th = ({ node, ...props }) => (
  <th className="border px-2 py-1 font-semibold text-left" {...props} />
);
const Td = ({ node, ...props }) => (
  <td className="border px-2 py-1" {...props} />
);

// Invert-aware overrides — memoised per bubble tone.
const buildInvertComponents = (invert) => {
  const linkColor = invert
    ? "text-white underline"
    : "text-emerald-700 underline";
  const codeBg = invert ? "bg-white/20 text-white" : "bg-slate-100 text-slate-800";
  const preBg = invert ? "bg-white/10 text-white" : "bg-slate-100 text-slate-800";
  const blockquoteBorder = invert ? "border-white/40" : "border-slate-300";

  const Link = ({ node, ...props }) => (
    <a
      target="_blank"
      rel="noopener noreferrer"
      className={linkColor}
      {...props}
    />
  );
  const Code = ({ inline, className: cn, children: kids, ...rest }) =>
    inline ? (
      <code className={`px-1 py-0.5 rounded ${codeBg} text-[12px]`} {...rest}>
        {kids}
      </code>
    ) : (
      <code
        className={`block px-2 py-1.5 rounded ${preBg} text-[12px] my-1 overflow-x-auto`}
        {...rest}
      >
        {kids}
      </code>
    );
  const Pre = ({ node, ...props }) => (
    <pre className={`rounded ${preBg} my-2 overflow-x-auto`} {...props} />
  );
  const Blockquote = ({ node, ...props }) => (
    <blockquote
      className={`border-l-2 pl-2 my-2 italic ${blockquoteBorder}`}
      {...props}
    />
  );
  return { a: Link, code: Code, pre: Pre, blockquote: Blockquote };
};

/**
 * Renders assistant chat text as markdown (bold, italics, lists, links, code).
 * Pass `invert` for light text on dark bubbles (user messages).
 */
const MarkdownMessage = ({ children, invert = false, className = "" }) => {
  const invertComponents = useMemo(() => buildInvertComponents(invert), [invert]);
  const components = useMemo(
    () => ({
      p: Paragraph,
      strong: Strong,
      em: Em,
      ul: Ul,
      ol: Ol,
      li: Li,
      h1: H1,
      h2: H2,
      h3: H3,
      hr: Hr,
      table: Table,
      th: Th,
      td: Td,
      ...invertComponents,
    }),
    [invertComponents]
  );

  return (
    <div
      className={`text-sm leading-relaxed break-words ${className}`}
      data-testid="markdown-message"
    >
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {children || ""}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownMessage;
